// src/components/ImportJobsList.jsx
// Journal des imports + annulation (ARTDAI §6 étape 9). Liste les jobs d'import du
// propriétaire (import_jobs, RLS client_id=auth.uid()) et permet d'annuler un import
// (edge import_undo : supprime les œuvres créées, marque le job cancelled).
import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";

const BORDER = "#e5e0d8";
const STATUS_TONE = {
  completed: "#2e7d32",
  completed_with_errors: "#a9843f",
  running: "#6b6459",
  cancelled: "#b3261e",
};

export default function ImportJobsList() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [jobs, setJobs] = useState(undefined);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState("");

  const fetchJobs = useCallback(async () => {
    if (!user?.id) { setJobs([]); return; }
    try {
      const { data } = await supabase
        .from("import_jobs")
        .select("id, source_filename, collection_title, rows_total, rows_inserted, rows_skipped, status, created_at")
        .eq("client_id", user.id)
        .order("created_at", { ascending: false })
        .limit(10);
      setJobs(Array.isArray(data) ? data : []);
    } catch { setJobs([]); }
  }, [user?.id]);
  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const undo = async (job) => {
    const ok = window.confirm(
      t("importJournal.confirm", { defaultValue: "Annuler cet import et supprimer les {{n}} œuvre(s) créées ? Action irréversible.", n: job.rows_inserted })
    );
    if (!ok) return;
    setBusy(job.id); setMsg("");
    try {
      const { data, error } = await supabase.functions.invoke("import_undo", { body: { job_id: job.id } });
      if (error || !data?.ok) throw error || new Error("undo");
      const base = t("importJournal.undone", { defaultValue: "{{n}} œuvre(s) supprimée(s).", n: data.deleted });
      const incomplete = data.failed > 0
        ? ` ${t("importJournal.incomplete", { defaultValue: "{{n}} œuvre(s) n'ont pas pu être supprimées : réessayez ou contactez-nous.", n: data.failed })}`
        : "";
      const files = ` ${t("importJournal.filesKept", { defaultValue: "Les fichiers envoyés dans le stockage ne sont pas supprimés." })}`;
      setMsg(base + incomplete + files);
      await fetchJobs();
    } catch {
      setMsg(t("importJournal.error", { defaultValue: "Échec de l'annulation." }));
    } finally {
      setBusy(null);
    }
  };

  if (jobs === undefined || jobs.length === 0) return null;

  const statusLabel = (s) => t(`importJournal.status.${s}`, { defaultValue: s });
  const fmtDate = (d) => { try { return new Date(d).toLocaleString("fr-FR"); } catch { return d; } };

  return (
    <section style={{ border: `1px solid ${BORDER}`, borderRadius: 14, padding: 18, background: "#faf9f7", marginTop: 24 }}>
      <h3 style={{ fontSize: 17, fontWeight: 700, color: "#1a1a1a", margin: "0 0 4px", fontFamily: "Georgia, serif" }}>
        {t("importJournal.title", { defaultValue: "Journal des imports" })}
      </h3>
      <p style={{ fontSize: 12.5, color: "#8a8378", margin: "0 0 12px" }}>
        {t("importJournal.subtitle", { defaultValue: "Vos imports récents. Vous pouvez annuler un import (les œuvres créées seront supprimées)." })}
      </p>
      {msg && <div style={{ fontSize: 13, color: "#2e7d32", marginBottom: 10 }}>{msg}</div>}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: "left", color: "#8a8378" }}>
              <th style={{ padding: "6px 8px", fontWeight: 600 }}>{t("importJournal.col.file", { defaultValue: "Fichier" })}</th>
              <th style={{ padding: "6px 8px", fontWeight: 600 }}>{t("importJournal.col.collection", { defaultValue: "Collection" })}</th>
              <th style={{ padding: "6px 8px", fontWeight: 600 }}>{t("importJournal.col.date", { defaultValue: "Date" })}</th>
              <th style={{ padding: "6px 8px", fontWeight: 600 }}>{t("importJournal.col.result", { defaultValue: "Résultat" })}</th>
              <th style={{ padding: "6px 8px", fontWeight: 600 }}>{t("importJournal.col.status", { defaultValue: "Statut" })}</th>
              <th style={{ padding: "6px 8px", fontWeight: 600 }} />
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => (
              <tr key={j.id} style={{ borderTop: `1px solid ${BORDER}` }}>
                <td style={{ padding: "7px 8px" }}>{j.source_filename || "—"}</td>
                <td style={{ padding: "7px 8px", color: "#6b6459" }}>{j.collection_title || "—"}</td>
                <td style={{ padding: "7px 8px", color: "#6b6459", whiteSpace: "nowrap" }}>{fmtDate(j.created_at)}</td>
                <td style={{ padding: "7px 8px" }}>
                  {j.rows_inserted}/{j.rows_total}{j.rows_skipped ? ` · ${j.rows_skipped} ${t("importJournal.skipped", { defaultValue: "ignorées" })}` : ""}
                </td>
                <td style={{ padding: "7px 8px", color: STATUS_TONE[j.status] || "#1a1a1a", fontWeight: 600 }}>{statusLabel(j.status)}</td>
                <td style={{ padding: "7px 8px", textAlign: "right" }}>
                  {j.status !== "cancelled" && j.rows_inserted > 0 && (
                    <button
                      type="button"
                      onClick={() => undo(j)}
                      disabled={busy === j.id}
                      style={{ fontSize: 12.5, padding: "5px 10px", borderRadius: 6, border: "1px solid #e3c4c4", background: "#fff", color: "#991b1b", cursor: busy === j.id ? "default" : "pointer" }}
                    >
                      {busy === j.id ? t("importJournal.undoing", { defaultValue: "Annulation…" }) : t("importJournal.undo", { defaultValue: "Annuler l'import" })}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
